import * as React from 'react';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/shadcn/dialog';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/shadcn/drawer';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/components/ui/shadcn/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

/**
 * One overlay for record forms: a right-hand sheet on desktop, a bottom sheet on
 * mobile. `variant="dialog"` keeps the centred dialog on desktop instead — for
 * confirms, media previews, the command palette, and the row-anchored pickers
 * whose desktop branch is a dropdown anyway.
 *
 * The sub-parts mirror the `Dialog` family so migrating a call site is an import
 * swap plus wrapping the scrolling middle in `ResponsiveModalBody`. `Title` and
 * `Description` resolve to the real primitive of whichever root is active, so
 * Radix keeps wiring `aria-labelledby`/`aria-describedby`; the layout parts are
 * plain divs in every branch, exactly as shadcn's own header/footer are.
 *
 * `Sheet` is `@radix-ui/react-dialog` with different positioning classes, and
 * vaul's `Drawer.Root` wraps it too, so focus trap, Escape handling and body
 * scroll lock behave identically whichever branch is live. The one difference
 * that matters: vaul only fires `onOpenChange` for closes it initiates itself,
 * so a controlled close is silent on the mobile branch.
 */
type Surface = 'drawer' | 'sheet' | 'dialog';

const ResponsiveModalContext = React.createContext<Surface>('dialog');

const useSurface = () => React.useContext(ResponsiveModalContext);

type ResponsiveModalSize = 'md' | 'lg' | 'xl' | '2xl';

/** Desktop sheet widths, by how much the form has to say. */
const SHEET_WIDTHS: Record<ResponsiveModalSize, string> = {
    md: 'sm:w-[28rem] sm:max-w-[95vw]',
    lg: 'sm:w-[36rem] sm:max-w-[95vw]',
    xl: 'sm:w-[46rem] sm:max-w-[95vw]',
    // Line-item editors — offers, costs — that were multi-column dialogs.
    '2xl': 'sm:w-[64rem] sm:max-w-[95vw]',
};

interface ResponsiveModalProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children: React.ReactNode;
    /**
     * The desktop surface. Mobile is a bottom drawer either way.
     *
     * Record forms take the default. Pass `dialog` for the exceptions: confirms,
     * media previews, the command palette, and pickers anchored to a table row
     * (whose desktop branch is a dropdown, so only mobile sees an overlay).
     */
    variant?: 'sheet' | 'dialog';
}

function ResponsiveModal({ open, onOpenChange, children, variant = 'sheet' }: ResponsiveModalProps) {
    const isMobile = useIsMobile();
    const [mounted, setMounted] = React.useState(false);

    // useIsMobile() reports false on the first render so SSR markup matches, so
    // the branch is only trusted once we are certainly running on the client.
    React.useEffect(() => setMounted(true), []);

    const surface: Surface = mounted && isMobile ? 'drawer' : variant;
    const Root = surface === 'drawer' ? Drawer : surface === 'sheet' ? Sheet : Dialog;

    return (
        <ResponsiveModalContext.Provider value={surface}>
            <Root open={open} onOpenChange={onOpenChange}>
                {children}
            </Root>
        </ResponsiveModalContext.Provider>
    );
}

function ResponsiveModalTrigger(props: React.ComponentProps<typeof DialogTrigger>) {
    const surface = useSurface();
    const Trigger = surface === 'drawer' ? DrawerTrigger : surface === 'sheet' ? SheetTrigger : DialogTrigger;

    return <Trigger {...props} />;
}

function ResponsiveModalClose(props: React.ComponentProps<typeof DialogClose>) {
    const surface = useSurface();
    const Close = surface === 'drawer' ? DrawerClose : surface === 'sheet' ? SheetClose : DialogClose;

    return <Close {...props} />;
}

interface ResponsiveModalContentProps extends React.ComponentProps<typeof DialogContent> {
    /** Applied to the desktop surface only — widths mean nothing to the mobile drawer. */
    dialogClassName?: string;
    /** Applied to the mobile drawer only. */
    drawerClassName?: string;
    /** Desktop sheet width. Ignored by the dialog and drawer branches, which size themselves. */
    size?: ResponsiveModalSize;
}

function ResponsiveModalContent({
    className,
    dialogClassName,
    drawerClassName,
    size = 'lg',
    showCloseButton,
    children,
    ...props
}: ResponsiveModalContentProps) {
    const surface = useSurface();

    if (surface === 'drawer') {
        return (
            <DrawerContent
                className={cn('overflow-hidden data-[vaul-drawer-direction=bottom]:max-h-[92dvh]', className, drawerClassName)}
                {...props}
            >
                {children}
            </DrawerContent>
        );
    }

    if (surface === 'sheet') {
        return (
            <SheetContent
                side="right"
                // Full height by design — the header and footer pin, the body scrolls.
                className={cn('flex h-full flex-col gap-0 p-0', SHEET_WIDTHS[size], className, dialogClassName)}
                {...props}
            >
                {children}
            </SheetContent>
        );
    }

    return (
        <DialogContent
            // Padding moves to the header/body/footer parts so the body is the
            // only scroll container, matching how the sheet is laid out.
            className={cn('flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0', className, dialogClassName)}
            showCloseButton={showCloseButton}
            {...props}
        >
            {children}
        </DialogContent>
    );
}

function ResponsiveModalHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="responsive-modal-header"
            // pr-10 keeps the title clear of the dialog's absolute close button.
            className={cn('flex shrink-0 flex-col gap-1 border-b px-4 py-3 text-left md:gap-2 md:px-6 md:py-4 md:pr-10', className)}
            {...props}
        />
    );
}

function ResponsiveModalBody({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="responsive-modal-body"
            className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 md:px-6', className)}
            {...props}
        />
    );
}

function ResponsiveModalFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="responsive-modal-footer"
            className={cn(
                'bg-background flex shrink-0 flex-col-reverse gap-2 border-t px-4 pt-3',
                'pb-[max(env(safe-area-inset-bottom),0.75rem)] md:flex-row md:justify-end md:px-6 md:py-4 md:pb-4',
                className,
            )}
            {...props}
        />
    );
}

function ResponsiveModalTitle({ className, ...props }: React.ComponentProps<typeof DialogTitle>) {
    const surface = useSurface();
    const Title = surface === 'drawer' ? DrawerTitle : surface === 'sheet' ? SheetTitle : DialogTitle;

    return <Title className={cn('text-base leading-tight font-semibold md:text-lg', className)} {...props} />;
}

function ResponsiveModalDescription({ className, ...props }: React.ComponentProps<typeof DialogDescription>) {
    const surface = useSurface();
    const Description = surface === 'drawer' ? DrawerDescription : surface === 'sheet' ? SheetDescription : DialogDescription;

    return <Description className={cn('text-sm', className)} {...props} />;
}

export {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalClose,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
};
